import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { editCategoryInitialState, EditCategoryContext, TVisibleForm, TTypeOfModalWindow } from './editCategoryContext';
import { editCategoryReducer } from './editCategoryReducer';
import { EditCategoryActionTypes } from './editCategoryActions';
import { useFetchData } from 'hooks/useFetchData';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IDefaultLanguage,
	IGetCategoryInfo,
	IUpdateCategory,
	TCategoryType,
} from 'types';
import { getCategoryInfo } from 'backend/categories';

const EditCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editCategoryReducer, editCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const { response, loading, refetch } = useFetchData<IGetCategoryInfo, string>(
		categoryID ? getCategoryInfo : null,
		categoryID || '',
	);

	useNamedHeader(response?.data.category.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditCategoryActionTypes.SET_ALL_DATA,
				payload: {
					categoryID: categoryID ? categoryID : '',
					filePath: response.data.category.filePath,
					preSignedUrl: response.data.category.preSignedUrl,
					initialIDSelectedCustomer: response.data.category.customerId,
					initialIDsSelectedRestaurant: response.data.category.restaurantsCategories.map((el) => el.id),
					nameOfCategory: response.data.category.name,
					categoryType: response.data.category.categoryType,
					subcategoriesOfCategory: response.data.category.subCategories,
					isRestricted: response.data.category.isRestricted,
					activationSchedule: response.data.category.activationCron || '',
					deactivationSchedule: response.data.category.deactivationCron || '',
					translations: response.data.category.translations,
				},
			});
			dispatch({
				type: EditCategoryActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						IDSelectedCustomer: response.data.category.customerId,
						IDsSelectedRestaurant: response.data.category.restaurantsCategories.map((el) => el.id),
						nameOfCategory: response.data.category.name,
						categoryType: response.data.category.categoryType,
						isRestricted: response.data.category.isRestricted,
						activationSchedule: response.data.category.activationCron || '',
						deactivationSchedule: response.data.category.deactivationCron || '',
						filePath: response.data.category.filePath,
						translations: response.data.category.translations,
					},
				},
			});
		}
	}, [categoryID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditCategoryActionTypes.DELETE_PHOTO }), []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfCategory = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_NAME_OF_CATEGORY,
			payload: {
				nameOfCategory: value,
			},
		});
	}, []);

	const setCategoryType = useCallback((value: Nullable<TCategoryType>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_CATEGORY_TYPE,
			payload: {
				categoryType: value,
			},
		});
	}, []);

	const toggleIsRestricted = useCallback(() => {
		dispatch({
			type: EditCategoryActionTypes.TOGGLE_IS_RESTRICTED,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateCategory | null) => {
		dispatch({
			type: EditCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditCategoryContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				changeVisibleForm,
				setFileId,
				deletePhoto,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfCategory,
				setCategoryType,
				toggleIsRestricted,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				isLoading: state.isLoading,
				visibleForm: state.visibleForm,
				filePath: state.filePath,
				preSignedUrl: state.preSignedUrl,
				fileId: state.fileId,
				categoryID: state.categoryID,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfCategory: state.nameOfCategory,
				categoryType: state.categoryType,
				subcategoriesOfCategory: state.subcategoriesOfCategory,
				isRestricted: state.isRestricted,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditCategoryContext.Provider>
	);
};

export default EditCategoryState;
